import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Dropdown } from 'react-bootstrap'
import { Link } from 'react-router'
import Select from 'react-select'
import XLSX from 'xlsx'

import { GET, httpOk } from 'helpers/api'
import { returnSign, formatDate, findKeyFromArrayByValue, formatMoney } from 'helpers/helpers'
import { parseProductTableOrderBy, buildProductTableOrderBy } from 'helpers/productFilterParams'
import { renderSortIndicator } from 'helpers/tableSortIndicator'
import CustomTooltip from "pages/parts/CustomTooltip"
import useTableDragScroll from 'pages/parts/useTableDragScroll'
import PeriodPicker from 'pages/parts/PeriodPicker'
import { businessToday, businessMonthStart } from 'helpers/businessDay'

// Поля, которые у меню складываются из его блюд
const SUM_KEYS = [
	'quantity', 'sales', 'sales_share', 'gross_income', 'gross_share', 'cost',
	'expenses_by_gross', 'expenses_by_sales', 'full_cost_by_gross', 'full_cost_by_sales',
	'net_profit_by_gross', 'net_profit_by_sales',
]

// Рентабельность блюд: расходы периода распределяются на себестоимость блюд
// пропорционально доле в валовом доходе или в продажах. Считает бэкенд, оба варианта сразу —
// переключатель только выбирает, какие колонки показать.
function ProductProfitability() {
	const { t } = useTranslation()

	const reduxAccount = useSelector(state => state.account)

	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [filterData, setFilterData] = useState({
		start_date: businessMonthStart(),
		end_date: businessToday(),
		pos_id: reduxAccount.pos_id,
	})
	const [data, setData] = useState({ data: [] })
	const [poses, setPoses] = useState([])
	// gross — по валовому доходу, sales — по продажам
	const [base, setBase] = useState('gross')
	const [orderBy, setOrderBy] = useState(null)
	// Раскрытые меню: { [menu_id]: true }
	const [expanded, setExpanded] = useState({})
	const tableDragScroll = useTableDragScroll()

	// Колонки, зависящие от базы распределения
	const expensesKey = base === 'gross' ? 'expenses_by_gross' : 'expenses_by_sales'
	const fullCostKey = base === 'gross' ? 'full_cost_by_gross' : 'full_cost_by_sales'
	const netProfitKey = base === 'gross' ? 'net_profit_by_gross' : 'net_profit_by_sales'
	const unitFullCostKey = base === 'gross' ? 'unit_full_cost_by_gross' : 'unit_full_cost_by_sales'

	// Базы долей — как на бэкенде: в долю по валовому идёт только положительный валовый доход
	const shareBase = useMemo(() => {
		let sales = 0
		let gross = 0
		for (const item of data.data || []) {
			sales += Math.max(Number(item.sales) || 0, 0)
			gross += Math.max(Number(item.gross_income) || 0, 0)
		}
		return { sales, gross }
	}, [data.data])

	// Расходы больше валового дохода — заведение за период в убытке. При распределении
	// по валовому доходу прибыль блюда = валовый × (1 − расходы ÷ валовый), и тогда в минусе
	// все блюда разом. Без пояснения это читается как ошибка отчёта.
	const totalExpenses = Number(data.total_expenses) || 0
	const totalGross = Number(data.total_gross_income) || 0
	const expensesExceedGross = totalExpenses > 0 && totalExpenses > totalGross

	const shareKey = base === 'gross' ? 'gross_share' : 'sales_share'
	const shareLabel = base === 'gross' ? t('gross_income_share') : t('sales_share')
	const fm = (value, decimals = 0) => formatMoney(value, decimals)

	// formula — подсказка заголовка, explain(row) — та же формула на числах строки (подсказка ячейки)
	const columns = [
		{ key: 'product_name', label: t('name2'), text: true },
		{ key: 'quantity', label: t('quantity'), decimals: 2 },
		{
			key: 'sales', label: t('sales_amount'),
			formula: `Σ ${t('sale_price')} × ${t('quantity')}`,
		},
		{
			key: 'sales_share', label: t('sales_share') + ', %', decimals: 2,
			formula: `${t('sales_amount')} ÷ Σ ${t('sales_amount')} × 100`,
			explain: row => `${fm(Math.max(row.sales, 0))} ÷ ${fm(shareBase.sales)} × 100 = ${fm(row.sales_share, 2)}`,
		},
		{
			key: 'gross_income', label: t('gross_income'),
			formula: `${t('sales_amount')} − ${t('cost_price')}`,
			explain: row => `${fm(row.sales)} − ${fm(row.cost)} = ${fm(row.gross_income)}`,
		},
		{
			key: 'gross_share', label: t('gross_income_share') + ', %', decimals: 2,
			formula: `${t('gross_income')} ÷ Σ ${t('gross_income')} × 100`,
			explain: row => `${fm(Math.max(row.gross_income, 0))} ÷ ${fm(shareBase.gross)} × 100 = ${fm(row.gross_share, 2)}`,
		},
		{
			key: 'cost', label: t('cost_price'),
			formula: `Σ ${t('cost_price')} × ${t('quantity')}`,
		},
		{
			key: expensesKey, label: t('allocated_expenses'),
			formula: `${t('expenses')} × ${shareLabel}`,
			explain: row => `${fm(data.total_expenses)} × ${fm(row[shareKey], 2)}% = ${fm(row[expensesKey])}`,
		},
		{
			key: fullCostKey, label: t('full_cost_price'),
			formula: `${t('cost_price')} + ${t('allocated_expenses')}`,
			explain: row => `${fm(row.cost)} + ${fm(row[expensesKey])} = ${fm(row[fullCostKey])}`,
		},
		{
			key: netProfitKey, label: t('net_profit'), profit: true,
			formula: `${t('sales_amount')} − ${t('full_cost_price')}`,
			explain: row => `${fm(row.sales)} − ${fm(row[fullCostKey])} = ${fm(row[netProfitKey])}`,
		},
		{
			key: 'unit_cost', label: t('unit_cost_price'), unit: true,
			formula: `${t('cost_price')} ÷ ${t('quantity')}`,
			explain: row => `${fm(row.cost)} ÷ ${fm(row.quantity, 2)} = ${fm(row.unit_cost)}`,
		},
		{
			key: unitFullCostKey, label: t('unit_full_cost_price'), unit: true,
			formula: `${t('full_cost_price')} ÷ ${t('quantity')}`,
			explain: row => `${fm(row[fullCostKey])} ÷ ${fm(row.quantity, 2)} = ${fm(row[unitFullCostKey])}`,
		},
		{
			key: 'unit_sale_price', label: t('avg_sale_price'), unit: true,
			formula: `${t('sales_amount')} ÷ ${t('quantity')}`,
			explain: row => `${fm(row.sales)} ÷ ${fm(row.quantity, 2)} = ${fm(row.unit_sale_price)}`,
		},
	]

	function cellTitle(column, row, isMenu = false) {
		if (!column.explain || (column.unit && isMenu)) return undefined
		return column.explain(row)
	}

	// Строки сгруппированы по меню: итог меню — сумма его блюд (доли тоже складываются),
	// себестоимость на единицу у меню не считаем — у разных блюд она несопоставима.
	// Сортировка одна и та же: меню между собой и блюда внутри меню.
	const menus = useMemo(() => {
		const { field, sort } = parseProductTableOrderBy(orderBy)
		const mul = sort === 'desc' ? -1 : 1
		const compare = (a, b) => {
			if (field === 'product_name') {
				return mul * String(a[field] || '').localeCompare(String(b[field] || ''), undefined, { sensitivity: 'base', numeric: true })
			}
			return mul * ((Number(a[field]) || 0) - (Number(b[field]) || 0))
		}

		const map = {}
		for (const item of data.data || []) {
			const id = item.menu_id || 0
			if (!map[id]) {
				map[id] = { menu_id: id, product_name: item.menu_name || t('products_without_menu'), products: [] }
			}
			const menu = map[id]
			// Средняя цена продажи — считаем здесь, бэкенд её не отдаёт
			const quantity = Number(item.quantity) || 0
			item.unit_sale_price = quantity ? (Number(item.sales) || 0) / quantity : 0
			menu.products.push(item)
			for (const key of SUM_KEYS) {
				menu[key] = (menu[key] || 0) + (Number(item[key]) || 0)
			}
		}

		const list = Object.values(map)
		if (field) {
			list.sort(compare)
			list.forEach(menu => menu.products.sort(compare))
		} else {
			list.sort((a, b) => b.sales - a.sales)
		}
		return list
	}, [data.data, orderBy]) // eslint-disable-line react-hooks/exhaustive-deps

	const allExpanded = menus.length > 0 && menus.every(menu => expanded[menu.menu_id])

	function toggleMenu(menuId) {
		setExpanded({ ...expanded, [menuId]: !expanded[menuId] })
	}

	function toggleAll() {
		if (allExpanded) {
			setExpanded({})
			return
		}
		const next = {}
		menus.forEach(menu => { next[menu.menu_id] = true })
		setExpanded(next)
	}

	async function searchWithFilter() {
		var urlParams = ""

		if (filterData.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterData.pos_id
		if (filterData.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterData.start_date
		if (filterData.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterData.end_date

		if (urlParams !== "")
			setFilterDataExist(true)

		const response = await GET(`/report-product-profitability${urlParams}`, {}, { loader: true })
		if (httpOk(response)) setData(response.data)
		setDropdownIsOpen(false)
	}

	function handleSort(key) {
		const { field, sort } = parseProductTableOrderBy(orderBy)
		const nextSort = field === key ? (sort === 'asc' ? 'desc' : 'asc') : 'desc'
		setOrderBy(buildProductTableOrderBy(key, nextSort))
	}

	function formatCell(column, value, isMenu = false) {
		if (column.text) return value
		if (column.unit && isMenu) return ''
		if (column.decimals) return formatMoney(value, column.decimals)
		return formatMoney(value)
	}

	function exportToExcel() {
		var rows = [['#', ...columns.map(column => column.label)]]
		// Меню строкой, под ним его блюда с отступом
		menus.forEach((menu, index) => {
			rows.push([index + 1, ...columns.map(column => {
				if (column.text) return menu[column.key]
				if (column.unit) return ''
				return Number(menu[column.key]) || 0
			})])
			menu.products.forEach(item => {
				rows.push(['', ...columns.map(column => column.text ? '   ' + item[column.key] : (Number(item[column.key]) || 0))])
			})
		})
		rows.push([
			'', t('total'),
			Number(data.total_quantity) || 0,
			Number(data.total_sales) || 0,
			100,
			Number(data.total_gross_income) || 0,
			100,
			Number(data.total_cost) || 0,
			Number(data.total_expenses) || 0,
			Number(data['total_' + fullCostKey]) || 0,
			Number(data['total_' + netProfitKey]) || 0,
			'', '', '',
		])
		rows.push([])
		rows.push([t('allocation_base') + ': ' + (base === 'gross' ? t('by_gross_income') : t('by_sales'))])
		rows.push([t('period') + ': ' + formatDate(filterData.start_date) + ' — ' + formatDate(filterData.end_date)])

		const ws = XLSX.utils.aoa_to_sheet(rows);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "SheetJS");
		XLSX.writeFile(wb, t('product_profitability') + ".xlsx");
	}

	function resetFilter() {
		setFilterData({
			start_date: businessMonthStart(),
			end_date: businessToday(),
			pos_id: reduxAccount.pos_id,
		})
		setFilterDataExist(false)
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	useEffect(() => {
		getPoses()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const sortState = parseProductTableOrderBy(orderBy)

	return (
		<>
			<div className="page-header">
				<h4 className="vertical-center h-100">
					{t('product_profitability')}
				</h4>
			</div>

			<div className="card">
				<div className="card-body">

					<div className="d-flex justify-content-between flex-wrap gap-2 mb-3">
						<div className="d-flex flex-wrap gap-2">
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('pos')}</label>
												<Select
													options={poses}
													value={poses.find(option => option.id === filterData.pos_id) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'pos_id': option.id })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-12 mt-2">
												<label>{t('period')}</label>
												<PeriodPicker
													withTime={false}
													startDate={filterData.start_date}
													endDate={filterData.end_date}
													onChange={(period) => setFilterData({ ...filterData, ...period })}
												/>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary"
														onClick={() => setDropdownIsOpen(false)}>
														{t('cancel')}
													</button>
													<button className="btn btn-primary"
														onClick={() => searchWithFilter()}>
														{t('apply')}
													</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<CustomTooltip text={t('EXCEL')}>
									<button className="btn btn-outline-primary btn-wide-2"
										onClick={() => exportToExcel()}>
										<i className="uil uil-file-download-alt"></i>
									</button>
								</CustomTooltip>
							</div>
						</div>

						<div className="d-flex align-items-center gap-2">
							<span className="text-muted">{t('allocation_base')}:</span>
							<div className="btn-group">
								<button className={`btn ${base === 'gross' ? 'btn-primary' : 'btn-outline-primary'}`}
									onClick={() => setBase('gross')}>
									{t('by_gross_income')}
								</button>
								<button className={`btn ${base === 'sales' ? 'btn-primary' : 'btn-outline-primary'}`}
									onClick={() => setBase('sales')}>
									{t('by_sales')}
								</button>
							</div>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date && <span>{formatDate(filterData.start_date)}</span>}
										{(filterData.start_date && filterData.end_date) && <span> | </span>}
										{filterData.end_date && <span>{formatDate(filterData.end_date)}</span>}
									</span>
								}
								{filterData.pos_id &&
									<span className="me-2">
										<b>{t('pos')}: </b>
										{findKeyFromArrayByValue(poses, 'id', filterData.pos_id, 'name')}
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="d-flex flex-wrap gap-4 mb-2">
						<div><span className="text-muted">{t('sales_amount')}: </span><b>{formatMoney(data.total_sales)}</b></div>
						<div><span className="text-muted">{t('gross_income')}: </span><b>{formatMoney(data.total_gross_income)}</b></div>
						<div><span className="text-muted">{t('expenses')}: </span><b>{formatMoney(data.total_expenses)}</b></div>
						<div><span className="text-muted">{t('net_profit')}: </span><b>{formatMoney(data['total_' + netProfitKey])}</b></div>
					</div>
					<div className="text-muted fz-12 mb-3">{t('product_profitability_hint')}</div>

					{expensesExceedGross &&
						<div className="alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-2 py-2 mb-3">
							<span>
								{t('product_profitability_loss_warning', {
									expenses: formatMoney(totalExpenses),
									gross: formatMoney(totalGross),
								})}
							</span>
							<Link className="btn btn-sm btn-outline-dark"
								to={`/finance/profit-loss?start_date=${filterData.start_date}&end_date=${filterData.end_date}`}>
								{t('open_profit_loss')}
							</Link>
						</div>
					}

					{/* Прокрутка внутри таблицы: шапка, итоги и колонка названия закреплены */}
					<div className="table-responsive" style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}
						ref={tableDragScroll.ref}
						onMouseDown={tableDragScroll.onMouseDown}
						onMouseMove={tableDragScroll.onMouseMove}
						onMouseUp={tableDragScroll.onMouseUp}
						onMouseLeave={tableDragScroll.onMouseLeave}
						onClickCapture={tableDragScroll.onClickCapture}>
						<table className="table table-bordered table-hover table-sticky-head table-sticky-name mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center cursor" onClick={toggleAll}
										title={allExpanded ? t('collapse_all') : t('expand_all')}>
										<i className={`uil ${allExpanded ? 'uil-minus' : 'uil-plus'}`}></i>
									</th>
									{columns.map(column => (
										<th key={column.key}
											style={{ minWidth: column.text ? '12rem' : '8rem' }}
											className={`th-sortable user-select-none ${column.text ? '' : 'text-end'}`}
											onClick={() => handleSort(column.key)}
											title={column.formula ? '= ' + column.formula : t('sort_by')}>
											<div className={`d-flex align-items-center gap-1 ${column.text ? 'justify-content-between' : 'justify-content-end'}`}>
												<span>{column.label}</span>
												<span className="flex-shrink-0">{renderSortIndicator(column.key, { order_by: sortState.field, sort: sortState.sort })}</span>
											</div>
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{menus.map(menu => (
									<React.Fragment key={menu.menu_id}>
										<tr className="cursor" onClick={() => toggleMenu(menu.menu_id)}>
											<td className="text-center">
												<i className={`uil ${expanded[menu.menu_id] ? 'uil-minus' : 'uil-plus'}`}></i>
											</td>
											{columns.map(column => (
												<td key={column.key} title={cellTitle(column, menu, true)}
													className={`${column.text ? '' : 'text-end text-nowrap'} ${column.profit && Number(menu[column.key]) < 0 ? 'text-danger' : ''}`}>
													<b>{formatCell(column, menu[column.key], true)}</b>
													{column.text &&
														<span className="text-muted fz-12 ms-2">({menu.products.length})</span>
													}
												</td>
											))}
										</tr>
										{expanded[menu.menu_id] && menu.products.map(item => (
											<tr key={item.product_id}>
												<td></td>
												{columns.map(column => (
													<td key={column.key} title={cellTitle(column, item)}
														className={`${column.text ? 'ps-4' : 'text-end text-nowrap'} ${column.profit && Number(item[column.key]) < 0 ? 'text-danger' : ''}`}>
														{formatCell(column, item[column.key])}
													</td>
												))}
											</tr>
										))}
									</React.Fragment>
								))}
							</tbody>
							<tfoot>
								<tr className="table-active">
									<td></td>
									<td><b>{t('total')}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.total_quantity, 2)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.total_sales)}</b></td>
									<td></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.total_gross_income)}</b></td>
									<td></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.total_cost)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data.total_expenses)}</b></td>
									<td className="text-end text-nowrap"><b>{formatMoney(data['total_' + fullCostKey])}</b></td>
									<td className={`text-end text-nowrap ${Number(data['total_' + netProfitKey]) < 0 ? 'text-danger' : ''}`}>
										<b>{formatMoney(data['total_' + netProfitKey])}</b>
									</td>
									<td></td>
									<td></td>
									<td></td>
								</tr>
							</tfoot>
						</table>
					</div>
				</div>
			</div>
		</>
	)
}

export default ProductProfitability
